import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSettleOnce } from "../../../Admin/kReusableCoreTS/kSettleOnce";
import { DropboxIoRules } from "./DropboxIoRules";
import { AppImportStatus } from "./AppImportStatus";
import { UnlockStep } from "./AppUnlockRun";

// AppDropboxUpload - the unlocked database, sent from this page straight into the subscriber's own
// Dropbox. Every decision is `DropboxIoRules`'.
//
// ==================================================================================================
// !! ⭐ IT GOES DIRECTLY, AND NOT A BYTE OF IT PASSES THROUGH WACOMMANDER
// ==================================================================================================
//
// `StorageTicketRules`' header carries the full argument. The short form: this is tens to hundreds
// of megabytes of somebody's whole message history, the container holding the alternative is a
// single replica carrying fifty live WhatsApp sessions, and the product's premise is that the
// archive lives in an account the subscriber already owns. ⚠ Dropbox permits it - a CORS preflight
// from the published origin was measured answering 200 with `Dropbox-API-Arg` allowed.
//
// ⛔ THE TICKET CAN WRITE AND CANNOT READ, AND THAT WAS PROVED ON THE LIVE API before this class was
// written: the same narrowed token ran start, append, finish and delete, and was refused
// `files/download` with `missing_scope/files.content.read`.
//
// ==================================================================================================
// !! ⛔ SILENCE IS THE FAILURE MODE, SO EVERY CHUNK REPORTS
// ==================================================================================================
//
// This moves an archive over whatever connection a phone happens to have, at the last step of a
// sign-up. A quiet minute reads as a hung page, and a hung page gets closed - half way through, by
// somebody who then has no idea whether their history arrived. ⚠ The percentages are real: they
// count bytes Dropbox has ACKNOWLEDGED, not bytes handed to the browser.
//
// ⚠ WHY `XMLHttpRequest` AND NOT `fetch`: `XMLHttpRequest.upload` reports UPLOAD progress and
// `fetch` still does not. On a chunked upload of an 8 MB body that is the difference between a bar
// that moves and a bar that jumps once per chunk. The same conclusion is recorded in `AppSource.ts`
// for why neither is the shared SharePoint transport (law 2).

/** The end: "" is success, anything else is the sentence the subscriber sees. */
export type UploadDone = (Why: string) => void;

export class AppDropboxUpload {
    private Token: string;
    private Path: string;
    private Body: Uint8Array | Blob;
    /** The body's size in bytes, whichever kind it is (`SizeOf`). */
    private Total: number;
    private Step: UnlockStep;
    private Result: kSettleOnce<string>;
    private Session: string = "";
    private Sent: number = 0;
    /**
     * Dropbox's `rev` of the file this upload wrote, read off `finish`'s own answer - what the page then
     * asks the service about (`AppImportStatus`). "" until the upload finished, and "" when Dropbox's
     * answer carried none this page can use (the upload still succeeded).
     */
    public Rev: string = "";

    /**
     * ⭐ `Body` IS EITHER BYTES IN MEMORY (an unlocked `.crypt15`) OR A FILE ON DISK (`Blob` - an
     * iPhone's plaintext database, `ImportPlain`). A `Blob` is NEVER read into memory here: each
     * chunk is a `Blob.slice`, which the browser streams from the disk as it sends it - so a database
     * of several hundred megabytes costs this page nothing.
     */
    public constructor(Token: string, Path: string, Body: Uint8Array | Blob, Step: UnlockStep,
                       Done: UploadDone) {
        this.Token = Token;
        this.Path = Path;
        this.Body = Body;
        this.Total = AppDropboxUpload.SizeOf(Body);
        this.Step = Step;
        this.Result = new kSettleOnce<string>(Done);
    }

    /**
     * How many bytes a body holds - what the storage ticket's space check is asked about.
     * DOCUMENTED EXCEPTION: reads one property of a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper; null answers 0, which every caller refuses to send.
     */
    public static SizeOf = (Body: Uint8Array | Blob | null): number => {
        if (Body === null || Body === undefined) { return 0; }
        if (Body instanceof Uint8Array) { return Body.length; }
        return Number(Body.size);
    };

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            if (nz(this.Token).length === 0 || nz(this.Path).length === 0) {
                this.Settle("That upload could not be started. Nothing was changed.");
                return;
            }
            if (this.Body === null || this.Total <= 0) {
                this.Settle("There was nothing to send. Choose the backup again.");
                return;
            }
            this.Say();
            this.SendChunk(DropboxIoRules.StartUrl(), DropboxIoRules.StartArg(), this.OnStarted);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.Start");
            this.Settle("That upload could not be started.");
        }
    };

    /**
     * One chunk, from `this.Sent`.
     *
     * ⚠ THE SLICE IS A COPY, AND IT HAS TO BE. `Uint8Array.subarray` shares the underlying buffer,
     * and `XMLHttpRequest.send` on a view whose buffer is still referenced elsewhere is where an
     * upload of the wrong bytes comes from. `slice` costs one chunk of memory, once.
     * ⚠ A `Blob`'s `slice` is a REFERENCE to that part of the file - no bytes are read until the
     * browser sends them (see the constructor).
     */
    private SendChunk = (Url: string, Arg: string, Then: (Req: XMLHttpRequest) => void): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            if (nz(Arg).length === 0) {
                this.Settle("That upload could not be continued. Nothing was changed.");
                return;
            }
            const Len: number = DropboxIoRules.ChunkAt(this.Sent, this.Total);
            const Piece: Uint8Array | Blob = this.Body.slice(this.Sent, this.Sent + Len);
            const Call: UploadCall = new UploadCall(this, Then);
            const Req: XMLHttpRequest = new XMLHttpRequest();
            Req.open("POST", Url, true);
            Req.setRequestHeader("Authorization", "Bearer " + this.Token);
            Req.setRequestHeader("Dropbox-API-Arg", Arg);
            Req.setRequestHeader("Content-Type", "application/octet-stream");
            Req.upload.onprogress = Call.OnUpload;
            Req.onerror = Call.OnNetwork;
            Req.onload = Call.OnLoad;
            Req.send(Piece as XMLHttpRequestBodyInit);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.SendChunk");
            this.Settle("The upload stopped part way.");
        }
    };

    /**
     * ⛔ THE SESSION ID COMES BACK FROM `start` AND NOWHERE ELSE. Without it every later chunk
     * addresses no session at all, so an absent one stops rather than retries.
     */
    private OnStarted = (Req: XMLHttpRequest): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            if (Req.status !== 200) {
                this.Settle(DropboxIoRules.Why(Req.status, nz(Req.responseText)));
                return;
            }
            this.Session = DropboxIoRules.SessionIdIn(nz(Req.responseText));
            if (this.Session.length === 0) {
                this.Settle("Dropbox did not say where to send your history, so nothing was sent.");
                return;
            }
            this.Advance();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.OnStarted");
            this.Settle("The upload stopped part way.");
        }
    };

    private OnAppended = (Req: XMLHttpRequest): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            if (Req.status !== 200) {
                this.Settle(DropboxIoRules.Why(Req.status, nz(Req.responseText)));
                return;
            }
            this.Advance();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.OnAppended");
            this.Settle("The upload stopped part way.");
        }
    };

    /**
     * Count what Dropbox just accepted, then send the next chunk or close the session.
     *
     * ⛔ THE COUNT IS ADVANCED ONLY AFTER A 200, which is what makes the offset the number of bytes
     * Dropbox actually HOLDS rather than the number this page handed to the network. Dropbox
     * validates it - an append at the wrong offset is refused - so a count advanced optimistically
     * turns a recoverable retry into a refusal blaming the subscriber.
     */
    private Advance = (): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            this.Sent = this.Sent + DropboxIoRules.ChunkAt(this.Sent, this.Total);
            this.Say();
            if (this.Sent < this.Total) {
                this.SendChunk(DropboxIoRules.AppendUrl(),
                    DropboxIoRules.AppendArg(this.Session, this.Sent), this.OnAppended);
                return;
            }
            this.Finish();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.Advance");
            this.Settle("The upload stopped part way.");
        }
    };

    /** ⚠ `finish` CARRIES NO BYTES - see `DropboxIoRules`' header for why the last chunk is not
     *  folded into it. */
    private Finish = (): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            const Arg: string =
                DropboxIoRules.FinishArg(this.Session, this.Sent, this.Path);
            if (Arg.length === 0) {
                this.Settle("That upload could not be completed. Nothing was changed.");
                return;
            }
            const Call: UploadCall = new UploadCall(this, this.OnFinished);
            const Req: XMLHttpRequest = new XMLHttpRequest();
            Req.open("POST", DropboxIoRules.FinishUrl(), true);
            Req.setRequestHeader("Authorization", "Bearer " + this.Token);
            Req.setRequestHeader("Dropbox-API-Arg", Arg);
            Req.setRequestHeader("Content-Type", "application/octet-stream");
            Req.onerror = Call.OnNetwork;
            Req.onload = Call.OnLoad;
            Req.send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.Finish");
            this.Settle("The upload could not be completed.");
        }
    };

    /**
     * ⛔ THE FILE DOES NOT EXIST UNTIL `finish` ANSWERS 200. An upload session that was never
     * finished leaves nothing in the folder, which is the right outcome - a half-written database
     * would import as a silently incomplete history. Reporting success here on anything else would
     * tell a subscriber their history had arrived when no file exists at all.
     */
    private OnFinished = (Req: XMLHttpRequest): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle("WACommander cannot send your history just now.");
            return;
        }
        try {
            if (Req.status !== 200) {
                this.Settle(DropboxIoRules.Why(Req.status, nz(Req.responseText)));
                return;
            }
            this.Rev = AppImportStatus.RevIn(nz(Req.responseText));
            this.Step("Your history is in your Dropbox.", 100);
            this.Settle("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.OnFinished");
            this.Settle("The upload could not be completed.");
        }
    };

    /** Progress within the chunk in flight, on top of what Dropbox already holds. */
    public OnChunkProgress = (Loaded: number): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a progress tick that fails must not end the transfer. The
        // bytes keep going either way; only the number on screen would be stale.
        try {
            this.Step("Sending your history to your Dropbox…",
                DropboxIoRules.Percent(this.Sent + Loaded, this.Total));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.OnChunkProgress");
        }
    };

    private Say = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Step("Sending your history to your Dropbox…",
                DropboxIoRules.Percent(this.Sent, this.Total));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.Say");
        }
    };

    /**
     * ⛔ A DROPPED CONNECTION IS NOT A FAULT TO REPORT. A phone loses its signal constantly, and
     * flipping the error gate would blank the page instead of letting them press it again.
     */
    public OnLost = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: an expected condition on a phone - fate 1, answered to the
        // caller as a sentence they can act on.
        this.Settle(DropboxIoRules.Why(0, ""));
    };

    /** ⛔ ONE ANSWER, whatever combination of events a failing request fires. */
    private Settle = (Why: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this is the only thing that answers the
        // caller, and a guard here would leave the page on "Sending..." for ever.
        try {
            this.Result.Settle(nz(Why));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxUpload.Settle");
        }
    };
}

/**
 * One request's three events, named.
 *
 * ⚠ IT IS A CLASS RATHER THAN THREE INLINE HANDLERS because the house rule forbids anonymous
 * functions, and because `Then` differs per call site while the other two never do - so the two
 * that are always the same cannot drift.
 */
class UploadCall {
    private Owner: AppDropboxUpload;
    private Then: (Req: XMLHttpRequest) => void;

    public constructor(Owner: AppDropboxUpload, Then: (Req: XMLHttpRequest) => void) {
        this.Owner = Owner;
        this.Then = Then;
    }

    public OnLoad = (E: ProgressEvent): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the run is blocked on this callback.
        try {
            this.Then(E.target as XMLHttpRequest);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "UploadCall.OnLoad");
        }
    };

    public OnNetwork = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: see `AppDropboxUpload.OnLost` - a lost signal is fate 1.
        this.Owner.OnLost();
    };

    public OnUpload = (E: ProgressEvent): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a progress tick cannot be allowed to end a transfer.
        try {
            this.Owner.OnChunkProgress(E.loaded);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "UploadCall.OnUpload");
        }
    };
}
